import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useGoogleLogin } from '@react-oauth/google';

import { ActionText } from '@components/action-text';
import { Button } from '@components/button';
import { createForm } from '@components/form';
import { IconBrandGoogle } from '@components/icons/brand-google';
import { IconBrandMicrosoft } from '@components/icons/brand-microsoft';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconInfoSmall } from '@components/icons/info-small';
import { IconSSO } from '@components/icons/sso';
import { InfoBox } from '@components/info-box';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import useQueryParams from '@hooks/use-query-params';
import { useScopedTranslate } from '@locale/translate';

import { config } from 'app-config';

import { LoginErrors, useLogin } from 'api/authentication/hooks';
import { LoginData } from 'api/authentication/requests';

import { Title } from './common';

import { routePaths } from '../navigation';

const Form = createForm<LoginData>();

const ErrorBox = ({ text }: { text: string }) => {
  return <InfoBox variant="red" text={text} icon={<IconInfoSmall />} />;
};

export const SSOErrors = () => {
  const { t } = useScopedTranslate(locale);
  const error = useQueryParams('error');
  switch (error) {
    case 'missingGoogleToken':
      return <ErrorBox text={t('missingGoogleToken')} />;
    case 'missingGoogleUserInfo':
      return <ErrorBox text={t('missingGoogleUserInfo')} />;
    case 'missingGoogleCode':
      return <ErrorBox text={t('missingGoogleCode')} />;
    case 'noActiveGoogleUser':
      return <ErrorBox text={t('noActiveUser')} />;
    case 'missingMicrosoftToken':
      return <ErrorBox text={t('missingMicrosoftToken')} />;
    case 'missingMicrosoftUserInfo':
      return <ErrorBox text={t('missingMicrosoftUserInfo')} />;
    case 'missingMicrosoftCode':
      return <ErrorBox text={t('missingMicrosoftCode')} />;
    case 'hasMicrosoftEnterprise':
      return <ErrorBox text={t('hasMicrosoftEnterprise')} />;
    case 'noActiveMicrosoftUser':
      return <ErrorBox text={t('noActiveUser')} />;
  }
  return null;
};

const baseRedirectURI = `${config.API_URL}/v1/auth/callback`;

export const SigninPage = () => {
  const { t } = useScopedTranslate(locale);
  const [errors, setErrors] = useState<LoginErrors>({});
  const location = useLocation();
  const login = useLogin(setErrors, location.state?.from?.pathname);

  const fromPathname = location.state?.from?.pathname;
  const isTemplateSignIn = fromPathname?.startsWith('/templates') ?? false;
  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    ux_mode: 'redirect',
    redirect_uri: `${baseRedirectURI}/google/`,
  });
  const microsoftURL = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize
    ?client_id=330c6a64-65e6-4f8a-b78e-4a7f610429a7
    &response_type=code
    &redirect_uri=${baseRedirectURI}/microsoft/
    &response_mode=query
    &scope=openid profile email User.Read
  `;

  return (
    <Stack space={4}>
      <Stack orientation="horizontal" justify="center">
        <Title>{t('title')}</Title>
      </Stack>
      <Stack space={4}>
        <SSOErrors />
        <Stack space={2}>
          <Button
            iconLeft={<IconBrandGoogle size="24" />}
            isFullWidth
            onClick={() => googleLogin()}
            text={t('signinGoogle')}
            variant="secondary"
          />
          <Button
            iconLeft={<IconBrandMicrosoft size="24" />}
            isFullWidth
            href={microsoftURL}
            text={t('signinMicrosoft')}
            variant="secondary"
          />
          <Button
            iconLeft={<IconSSO size="24" />}
            isFullWidth
            href={`/${routePaths.signinAzure}`}
            text={t('signinMicrosoftEnterprise')}
            variant="secondary"
          />
        </Stack>
      </Stack>
      <Stack space={2} orientation="horizontal" justify="center" align="center">
        <Separator />
        <Text font="semibold14" color="textSecondary" text={t('or')} />
        <Separator />
      </Stack>
      <Form
        updateErrors={errors}
        onSubmit={(data) => login.mutate(data)}
        isDisabled={login.isLoading}
      >
        <Form.TextField data-cy="email" id="email" label={t('core.email')} validators={['email']} />
        <Form.PasswordField data-cy="password" id="password" label={t('password')} />
        <Form.Submit data-cy="sign-in-button" variant="primary-blue">
          {t('signin')}
        </Form.Submit>
      </Form>
      {isTemplateSignIn && (
        <InfoBox
          text={t('pleaseSignInInfo')}
          icon={<IconFormIndicatorInfo color="indigo100" />}
          variant="indigo"
        />
      )}
      <Stack space={2} align="center">
        <ActionText.Link
          to={`/${routePaths.forgotpassword}`}
          children={t('forgotPassword')}
          font="regular14"
          color="textHelper"
        />
        <ActionText.Link
          to={`/${routePaths.signupCompany}`}
          font="regular14"
          color="textHelper"
          children={t('createAnAccount')}
        />
      </Stack>
    </Stack>
  );
};

const locale = {
  en: {
    title: 'Welcome back!',
    password: 'Password',
    signin: 'Sign in',
    signinGoogle: 'Sign in with Google',
    missingGoogleToken: 'Failed to obtain token from Google',
    missingGoogleUserInfo: 'Failed to obtain user info from Google',
    missingGoogleCode: 'Authorization code not provided',
    signinMicrosoft: 'Sign in with Microsoft',
    missingMicrosoftToken: 'Failed to obtain token from Microsoft',
    missingMicrosoftUserInfo: 'Failed to obtain user info from Microsoft',
    missingMicrosoftCode: 'Authorization code not provided',
    hasMicrosoftEnterprise: 'Use the enterprise sign in',
    signinMicrosoftEnterprise: 'Sign in with SSO',
    noActiveUser: 'No active user exists for this account',
    forgotPassword: 'Forgot password?',
    or: 'OR',
    pleaseSignInInfo: 'You are not signed in, please sign in to get the template',
    createAnAccount: 'Create an account',
  },
};
